import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getQuiz, StoredQuiz, saveGameResult } from '../services/quizStorageService';
import { getValidToken } from '../utils/tokenManager';
import Header from '../components/Header';
import ServerStatus from '../components/ServerStatus';
import GameStatusCard from '../components/GameStatusCard';
import AudioControls from '../components/AudioControls';
import PhaseContent from '../components/PhaseContent';
import LateJoinCard from '../components/LateJoinCard';
import { environment } from '../config/environment';
import { useAudioManager } from '../hooks/useAudioManager';

type Phase = 'LOBBY' | 'COUNTDOWN' | 'READING' | 'QUESTION' | 'REVEAL' | 'ACHIEVEMENTS' | 'INTERMISSION' | 'PODIUM';

interface Player {
  id: string;
  name: string;
  score: number;
}

interface LobbyUpdate {
  gamePin: string;
  phase: Phase;
  players: Player[];
  host: string;
  theme?: string;
}

interface QuestionPush {
  index: number;
  total: number;
  id: string;
  text: string;
  choices: string[];
  secondsLeft: number;
}

interface RevealPush {
  questionId: string;
  correctIdx: number;
  evidence: string;
}

interface LeaderboardPush {
  phase: Phase;
  players: Player[];
}

interface PodiumPush {
  winners: Player[];
}

interface Achievement {
  playerId: string;
  playerName: string;
  type: string;
  title: string;
  description: string;
  bonusPoints: number;
}

interface AchievementsPush {
  phase: Phase;
  achievements: Achievement[];
  secondsLeft: number;
}

const PlayQuiz: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [quiz, setQuiz] = useState<StoredQuiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Game state
  const [gamePin, setGamePin] = useState<string>('');
  const [phase, setPhase] = useState<Phase>('LOBBY');
  const [players, setPlayers] = useState<Player[]>([]);
  const [countdownMessage, setCountdownMessage] = useState<string>('');
  const [readingQuestion, setReadingQuestion] = useState<string>('');
  const [currentQuestion, setCurrentQuestion] = useState<QuestionPush | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [reveal, setReveal] = useState<RevealPush | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [leaderboard, setLeaderboard] = useState<Player[]>([]);
  const [winners, setWinners] = useState<Player[]>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<string>('classic');
  const [musicEnabled, setMusicEnabled] = useState(true);
  const [serverStatusCollapsed, setServerStatusCollapsed] = useState(true);
  const [gameStatistics, setGameStatistics] = useState<any>(null);
  const [lateJoinCardOpen, setLateJoinCardOpen] = useState(false);
  const [lateJoinEnabled, setLateJoinEnabled] = useState(true);

  const wsRef = useRef<WebSocket | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Sound effects manager
  const { playSound, stopSound, preloadSounds, toggleMute, isMuted, registerBackgroundMusic } = useAudioManager();

  // Handle background music based on theme
  useEffect(() => {
    // Theme background music mapping - only classic theme is supported
    const themeBackgroundMusic: Record<string, string | undefined> = {
      'classic': 'https://quizriot-prod-play-static-website-962595531541.s3.us-east-2.amazonaws.com/media/8bitretro.mp3',
    };

    const musicUrl = themeBackgroundMusic[currentTheme];

    console.log('🎵 Theme Music Debug:', {
      currentTheme,
      musicUrl,
      gameStarted,
      wsConnected,
      musicEnabled,
      willPlayMusic: !!(musicUrl && gameStarted && wsConnected && musicEnabled)
    });

    // Stop and cleanup previous audio
    if (audioRef.current) {
      console.log('🔇 Stopping previous audio');
      audioRef.current.pause();
      registerBackgroundMusic(null);
      audioRef.current = null;
    }

    // Start new music if available, game is active, and music is enabled
    if (musicUrl && gameStarted && wsConnected && musicEnabled) {
      console.log('🎶 Starting new audio:', musicUrl);
      const audio = new Audio(musicUrl);
      audio.loop = true;
      audio.volume = 0.3; // Set default volume (will be ducked to 20% during sound effects)

      audio.play().catch(err => {
        console.error('❌ Audio playback failed:', err);
      });

      audioRef.current = audio;
      registerBackgroundMusic(audio);
    } else {
      console.log('⏸️ Not starting music. Reasons:', {
        noMusicUrl: !musicUrl,
        gameNotStarted: !gameStarted,
        wsNotConnected: !wsConnected,
        musicDisabled: !musicEnabled
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        registerBackgroundMusic(null);
        audioRef.current = null;
      }
    };
  }, [currentTheme, gameStarted, wsConnected, musicEnabled, registerBackgroundMusic]);

  useEffect(() => {
    if (!isAuthenticated || !quizId) {
      navigate('/');
      return;
    }
    loadQuiz();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, quizId, navigate]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      setError(null);
      const quizData = await getQuiz(quizId!);
      setQuiz(quizData);
      // Auto-start the game
      setGameStarted(true);
      generateGamePin();
      // Preload sound effects
      preloadSounds();
    } catch (err) {
      console.error('Failed to load quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to load quiz');
    } finally {
      setLoading(false);
    }
  };

  const generateGamePin = () => {
    const pin = Math.random().toString(36).substring(2, 8).toUpperCase();
    setGamePin(pin);
  };

  const connectToGameServer = async () => {
    try {
      // Get a valid (refreshed if needed) ID token for authentication
      const idToken = await getValidToken();
      const wsUrl = `${environment.websocket.proxy}/game/${gamePin}?role=host${idToken ? `&token=${encodeURIComponent(idToken)}` : ''}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('Connected to game server');
        setWsConnected(true);
        setError(null);

        // Send quiz data and theme after connection is established
        if (quiz) {
          ws.send(JSON.stringify({
            type: 'set_quiz',
            data: quiz
          }));
        }

        // Always set classic theme
        ws.send(JSON.stringify({
          type: 'set_theme',
          data: { theme: 'classic' }
        }));
      };

      ws.onmessage = (event) => {
        try {
          handleGameMessage(JSON.parse(event.data));
        } catch {
          // Handle non-JSON status messages (e.g., "Game server not ready")
          if (event.data.includes('not ready') || event.data.includes('retry')) {
            setError('Game server is starting up, please wait...');
            setTimeout(async () => {
              if (wsRef.current?.readyState === WebSocket.CLOSED) {
                await connectToGameServer();
              }
            }, 3000);
          }
        }
      };

      ws.onclose = () => {
        console.log('Disconnected from game server');
        setWsConnected(false);
      };

      ws.onerror = (err) => {
        console.error('WebSocket error:', err);
        setError('Failed to connect to game server. Retrying...');
        setWsConnected(false);
        // Retry connection after delay
        setTimeout(async () => {
          await connectToGameServer();
        }, 3000);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to connect:', err);
      setError('Failed to connect to game server');
    }
  };

  const handleGameMessage = (message: any) => {
    console.log('📨 Received game message:', message.type, message.data);
    switch (message.type) {
      case 'lobby':
        const lobbyData: LobbyUpdate = message.data;
        setPhase(lobbyData.phase);
        setPlayers(lobbyData.players);
        if (lobbyData.theme) {
          console.log('🎨 Theme update from server:', lobbyData.theme);
          setCurrentTheme(lobbyData.theme);
        } else {
          console.log('⚠️ No theme in lobby data');
        }
        break;

      case 'quiz_set':
        console.log('Quiz data set on server:', message.data);
        break;

      case 'countdown':
        setPhase('COUNTDOWN');
        setCountdownMessage(message.data.message);
        break;

      case 'reading':
        setPhase('READING');
        setReadingQuestion(message.data.text);
        // Play gong sound at start of reading phase
        playSound('gong');
        break;

      case 'question':
        const questionData: QuestionPush = message.data;
        setCurrentQuestion(questionData);
        setPhase('QUESTION');
        setTimeLeft(questionData.secondsLeft);
        setReveal(null);
        startTimer();
        break;

      case 'reveal':
        const revealData: RevealPush = message.data;
        setReveal(revealData);
        setPhase('REVEAL');
        stopTimer();
        break;

      case 'achievements':
        const achievementsData: AchievementsPush = message.data;
        setAchievements(achievementsData.achievements || []);
        setPhase('ACHIEVEMENTS');
        // Play applause sound for achievements
        playSound('applause');
        break;

      case 'leaderboard':
        const leaderboardData: LeaderboardPush = message.data;
        setLeaderboard(leaderboardData.players);
        setPhase(leaderboardData.phase);
        break;

      case 'podium':
        const podiumData: PodiumPush = message.data;
        setWinners(podiumData.winners);
        setPhase('PODIUM');
        // Gracefully close WebSocket since game is complete
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        setWsConnected(false);
        // Stop any background music
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
        break;

      case 'game_statistics':
        console.log('📊 Received game statistics:', message.data);
        setGameStatistics(message.data.statistics);

        // Automatically save game results to DynamoDB
        if (quiz?.quiz_id && message.data.statistics) {
          const timestamp = new Date().toLocaleString();
          const label = `Game Result - ${timestamp}`;

          console.log('💾 Saving game result to database...', {
            quizId: quiz.quiz_id,
            label,
            statisticsKeys: Object.keys(message.data.statistics || {}),
          });

          saveGameResult(quiz.quiz_id, label, message.data.statistics)
            .then(() => {
              console.log('✅ Game result saved successfully', {
                quizId: quiz.quiz_id,
                label,
              });
            })
            .catch((err) => {
              console.error('❌ Failed to save game result - DETAILED DEBUG INFO:', {
                error: err,
                errorMessage: err?.message || 'Unknown error',
                errorName: err?.name || 'Unknown',
                quizId: quiz.quiz_id,
                label,
                timestamp,
                statisticsPayload: JSON.stringify(message.data.statistics, null, 2),
                stackTrace: err?.stack,
              });
              // Non-blocking error - don't interrupt the game flow
            });
        } else {
          console.warn('⚠️ Cannot save game statistics - missing required data:', {
            hasQuizId: !!quiz?.quiz_id,
            hasStatistics: !!message.data.statistics,
            quizId: quiz?.quiz_id,
          });
        }
        break;

      case 'error':
        const errorReason = message.data?.reason || 'Game error';
        // Show temporary error for game flow errors
        if (errorReason.includes('can only advance from intermission')) {
          setError('Please wait for the current phase to complete');
          setTimeout(() => setError(null), 3000);
        } else {
          setError(errorReason);
          // If it's a "game server not ready" error, attempt retry after delay
          if (errorReason.includes('Game server not ready') || errorReason.includes('retry')) {
            setTimeout(async () => {
              if (wsRef.current?.readyState === WebSocket.CLOSED) {
                setError('Retrying connection...');
                await connectToGameServer();
              }
            }, 3000);
          }
        }
        break;
    }
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        // Play heartbeat when 5 seconds remain
        if (prev === 6) { // Trigger at 6 so it plays when showing "5"
          playSound('heartbeat');
        }
        // Stop heartbeat when time runs out
        if (prev <= 1) {
          stopSound('heartbeat');
          stopTimer();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Also stop heartbeat sound when stopping timer
    stopSound('heartbeat');
  };

  useEffect(() => {
    const initGameServer = async () => {
      // Don't reconnect if game is finished (PODIUM phase)
      if (gamePin && gameStarted && !wsConnected && phase !== 'PODIUM') {
        await connectToGameServer();
      }
    };
    initGameServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gamePin, gameStarted, wsConnected, phase]);

  const startQuiz = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'host_start' }));
    }
  };

  const nextQuestion = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'next' }));
    }
  };

  // Keyboard shortcut handler (J key to toggle late join card)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only trigger if not in an input field and game is active
      if (
        e.key === 'j' &&
        !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement).tagName) &&
        gameStarted &&
        phase !== 'LOBBY' &&
        phase !== 'PODIUM'
      ) {
        setLateJoinCardOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameStarted, phase]);

  useEffect(() => {
    return () => {
      stopTimer();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">
          <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          Loading quiz...
        </div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600">Quiz not found</h1>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Error Message Toast */}
      {error && (
        <div className="fixed top-20 right-4 z-50 max-w-md">
          <div className="bg-red-50 border-2 border-red-200 text-red-700 rounded-lg p-4 shadow-lg">
            {error}
          </div>
        </div>
      )}

      {/* Floating Late Join Button - Only show after lobby and before podium */}
      {gameStarted && phase !== 'LOBBY' && phase !== 'PODIUM' && (
        <button
          onClick={() => setLateJoinCardOpen(true)}
          className="fixed top-24 right-6 z-30 w-14 h-14 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 flex items-center justify-center group"
          title="Show join info (Press J)"
        >
          <span className="text-2xl font-bold">+</span>
          <span className="absolute right-full mr-2 px-3 py-1 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Join Info (J)
          </span>
        </button>
      )}

      {/* Late Join Card Modal */}
      <LateJoinCard
        gamePin={gamePin}
        isOpen={lateJoinCardOpen}
        onClose={() => setLateJoinCardOpen(false)}
        lateJoinEnabled={lateJoinEnabled}
        onToggleLateJoin={() => setLateJoinEnabled(!lateJoinEnabled)}
      />

      <div className="container mx-auto px-6 py-6">
        <div className="flex gap-6 flex-col lg:flex-row">

          {/* Main Content Area - 70% on desktop */}
          <main className="flex-1 lg:order-1 order-2">
            <PhaseContent
              phase={phase}
              wsConnected={wsConnected}
              gamePin={gamePin}
              players={players}
              countdownMessage={countdownMessage}
              readingQuestion={readingQuestion}
              currentQuestion={currentQuestion}
              timeLeft={timeLeft}
              reveal={reveal}
              achievements={achievements}
              leaderboard={leaderboard}
              winners={winners}
              gameStatistics={gameStatistics}
            />
          </main>

          {/* Sidebar - 30% on desktop, sticky */}
          <aside className="lg:w-80 w-full lg:order-2 order-1 lg:sticky lg:top-20 lg:self-start">
            {quiz && (
              <GameStatusCard
                quiz={quiz}
                gamePin={gamePin}
                players={players}
                phase={phase}
                wsConnected={wsConnected}
                onStartQuiz={startQuiz}
                onNextQuestion={nextQuestion}
              />
            )}

            {gameStarted && (
              <>
                <ServerStatus
                  wsConnected={wsConnected}
                  wsReadyState={wsRef.current?.readyState}
                  gamePin={gamePin}
                  phase={phase}
                  playerCount={players?.length || 0}
                  error={error}
                  isCollapsed={serverStatusCollapsed}
                  onToggle={() => setServerStatusCollapsed(!serverStatusCollapsed)}
                />

                <AudioControls
                  musicEnabled={musicEnabled}
                  isMuted={isMuted}
                  onToggleMusic={() => setMusicEnabled(!musicEnabled)}
                  onToggleMute={toggleMute}
                />
              </>
            )}
          </aside>

        </div>
      </div>
    </div>
  );
};

export default PlayQuiz;